import Dexie, { Table } from 'dexie';

// Audio recording interface
export interface AudioRecording {
  id: string;
  blob: Blob;
  createdAt: number;
  duration: number;
  metadata?: {
    mimeType: string;
    size: number;
  };
  reportId?: number;
}

// Storage statistics interface
export interface StorageStats {
  count: number;
  recordings: AudioRecording[];
  totalSize: number;
}

const MAX_RECORDINGS = 3;

/**
 * Dexie-based AudioDatabase for storing audio recordings in IndexedDB
 * Replaces the raw IndexedDB implementation with a cleaner, promise-based API
 */
export class AudioDatabase extends Dexie {
  recordings!: Table<AudioRecording, string>;
  private channel: BroadcastChannel;

  constructor() {
    super('consultnow-audio');

    // Define schema
    this.version(1).stores({
      recordings: 'id, createdAt, reportId',
    });

    // BroadcastChannel for cross-tab communication
    this.channel = new BroadcastChannel('audio-storage');
  }

  /**
   * Save recording with automatic cleanup if limit exceeded
   */
  async save(recording: Omit<AudioRecording, 'id' | 'createdAt'>): Promise<string> {
    // Check current count
    const count = await this.recordings.count();

    // If at limit, delete oldest recording
    if (count >= MAX_RECORDINGS) {
      const oldest = await this.recordings.orderBy('createdAt').first();
      if (oldest?.id) {
        await this.deleteRecording(oldest.id);
      }
    }

    const id = `recording-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newRecording: AudioRecording = {
      ...recording,
      id,
      createdAt: Date.now(),
      metadata: {
        size: recording.blob.size,
        mimeType: recording.blob.type,
      },
    };

    await this.recordings.add(newRecording);

    // Notify other tabs
    this.channel.postMessage({
      type: 'recording-saved',
      id,
    });

    return id;
  }

  /**
   * Get recording by ID
   */
  async get(id: string): Promise<AudioRecording | undefined> {
    return this.recordings.get(id);
  }

  /**
   * Get all recordings, oldest first.
   *
   * Insertion-order ordering matters because the recovery effect on the
   * Quick Start / New Encounter pages numbers entries as "Recording N"
   * based on the position the merge helper sees. Without an explicit
   * ``orderBy``, Dexie returns rows in primary-key b-tree order, which
   * is not guaranteed chronological.
   */
  async getAll(): Promise<AudioRecording[]> {
    return this.recordings.orderBy('createdAt').toArray();
  }

  /**
   * Delete recording by ID
   */
  async deleteRecording(id: string): Promise<void> {
    await this.recordings.delete(id);

    // Notify other tabs
    this.channel.postMessage({
      type: 'recording-deleted',
      id,
    });
  }

  /**
   * Delete all recordings
   */
  async deleteAll(): Promise<void> {
    await this.recordings.clear();

    // Notify other tabs
    this.channel.postMessage({
      type: 'recordings-cleared',
    });
  }

  /**
   * Get storage statistics
   */
  async getStats(): Promise<StorageStats> {
    const recordings = await this.getAll();
    const totalSize = recordings.reduce((sum, rec) => sum + (rec.metadata?.size || 0), 0);

    return {
      totalSize,
      recordings,
      count: recordings.length,
    };
  }

  /**
   * Check if storage has recordings
   */
  async hasRecordings(): Promise<boolean> {
    const count = await this.recordings.count();
    return count > 0;
  }

  /**
   * Subscribe to storage changes from other tabs
   */
  onStorageChange(callback: (event: { type: string; id?: string }) => void): () => void {
    const handler = (event: MessageEvent) => {
      callback(event.data);
    };
    this.channel.addEventListener('message', handler);

    // Return unsubscribe function
    return () => {
      this.channel.removeEventListener('message', handler);
    };
  }

  /**
   * Close database and broadcast channel
   */
  closeAll(): void {
    this.close();
    this.channel.close();
  }
}

// Export singleton instance
export const audioDB = new AudioDatabase();

// Helper functions for backward compatibility
export const audioStorage = {
  save: (recording: Omit<AudioRecording, 'id' | 'createdAt'>) => audioDB.save(recording),
  get: (id: string) => audioDB.get(id),
  getAll: () => audioDB.getAll(),
  delete: (id: string) => audioDB.deleteRecording(id),
  deleteAll: () => audioDB.deleteAll(),
  getStats: () => audioDB.getStats(),
  hasRecordings: () => audioDB.hasRecordings(),
  onStorageChange: (callback: (event: { type: string; id?: string }) => void) => audioDB.onStorageChange(callback),
  close: () => audioDB.closeAll(),
};
